import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  FiUsers,
  FiBriefcase,
  FiHeart,
  FiAlertTriangle,
  FiShoppingBag,
  FiTrendingUp,
  FiUserCheck,
  FiClock,
  FiUserX,
  FiDollarSign,
  FiActivity,
  FiArrowDown,
} from 'react-icons/fi';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  Card,
  StatCard,
  Badge,
  Button,
  CsvExportButton,
} from '../../components/UI';
import {
  useDashboardStats,
  useUserGrowthData,
  useOrdersTrend,
} from '../../hooks';
import { getSubscriptionAnalytics } from '../../services/adminService';
import styles from './Dashboard.module.css';

const RANGE_OPTIONS = [
  { value: 7, label: '7d' },
  { value: 14, label: '14d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
  { value: 365, label: '1y' },
];

const Dashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialRange = parseInt(searchParams.get('range') || '14', 10);
  const [rangeDays, setRangeDays] = useState(
    RANGE_OPTIONS.some((o) => o.value === initialRange) ? initialRange : 14
  );

  const setRange = (value) => {
    setRangeDays(value);
    setSearchParams({ range: String(value) });
  };

  const { stats, loading: statsLoading, error } = useDashboardStats();
  const { growthData, loading: growthLoading } = useUserGrowthData(rangeDays);
  const { data: ordersTrend, loading: ordersLoading } = useOrdersTrend(rangeDays);

  // Subscription analytics — best-effort, separate from main stats so a slow
  // analytics CF doesn't block the rest of the dashboard.
  const [subAnalytics, setSubAnalytics] = useState(null);
  useEffect(() => {
    let alive = true;
    getSubscriptionAnalytics()
      .then((data) => { if (alive) setSubAnalytics(data?.analytics || data); })
      .catch((err) => console.warn('Subscription analytics failed:', err.message));
    return () => { alive = false; };
  }, []);

  const fmtMoney = (v) =>
    v == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Number(v));

  // Ledger-derived views. The analytics function returns a full year of daily
  // revenue; trim it to the selected range here so the picker drives this chart
  // too without another round trip.
  const revenueSeries = useMemo(() => {
    const all = subAnalytics?.revenueSeries || [];
    if (!all.length) return [];
    const cutoff = new Date(Date.now() - rangeDays * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
    return all.filter((row) => row.date >= cutoff);
  }, [subAnalytics, rangeDays]);

  const recentPayments = subAnalytics?.recentPayments || [];

  const tierRows = useMemo(
    () =>
      Object.entries(subAnalytics?.byTier || {})
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count),
    [subAnalytics]
  );

  const platformRows = useMemo(
    () =>
      Object.entries(subAnalytics?.byPlatform || {})
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count),
    [subAnalytics]
  );

  const RangePicker = () => (
    <div className={styles.rangePicker}>
      {RANGE_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={rangeDays === opt.value ? styles.active : ''}
          onClick={() => setRange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  const breakdownData = [
    {
      label: 'Businesses',
      Pending: stats.pendingBusinesses,
      Verified: stats.verifiedBusinesses,
      Rejected: stats.rejectedBusinesses,
    },
    {
      label: 'Vets',
      Pending: stats.pendingVets,
      Verified: stats.verifiedVets,
      Rejected: stats.rejectedVets,
    },
    {
      label: 'Reports',
      Pending: stats.pendingReports,
      Verified: stats.resolvedReports,
      Rejected: 0,
    },
  ];

  const csvRows = growthData.map((row, i) => ({
    date: row.date,
    new_users: row.count,
    new_orders: ordersTrend[i]?.count ?? 0,
  }));

  if (error) {
    return (
      <div className={styles.error}>
        <p>Error loading dashboard: {error.message}</p>
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Dashboard</h1>
          {statsLoading && <div className={styles.liveIndicator} />}
        </div>
        <p className={styles.subtitle}>
          Welcome back! Here's an overview of your platform.
          <span className={styles.liveText}> Live updates enabled</span>
        </p>
      </div>

      {/* Stats Grid */}
      <div className={styles.statsGrid}>
        <StatCard
          title="MRR"
          value={fmtMoney(subAnalytics?.mrr)}
          icon={FiDollarSign}
          color="primary"
        />
        <StatCard
          title="Active Subscriptions"
          value={subAnalytics?.activeCount ?? '—'}
          icon={FiActivity}
          color="secondary"
        />
        <StatCard
          title="Churn (30d)"
          value={subAnalytics?.churnRate != null ? `${subAnalytics.churnRate.toFixed(1)}%` : '—'}
          icon={FiArrowDown}
          color="warning"
        />
        <StatCard
          title="Total Users"
          value={stats?.totalUsers}
          icon={FiUsers}
          color="primary"
        />
        <StatCard
          title="New Users Today"
          value={stats?.newUsersToday}
          icon={FiTrendingUp}
          color="secondary"
        />
        <StatCard
          title="Active Users (7 days)"
          value={stats?.activeUsers}
          icon={FiUserCheck}
          color="tertiary"
        />
        <StatCard
          title="Banned Users"
          value={stats?.bannedUsers}
          icon={FiUserX}
          color="warning"
        />
        <StatCard
          title="Total Orders"
          value={stats?.totalOrders}
          icon={FiShoppingBag}
          color="primary"
        />
        <StatCard
          title="Orders This Week"
          value={stats?.ordersThisWeek}
          icon={FiTrendingUp}
          color="secondary"
        />
        <StatCard
          title="Verified Businesses"
          value={stats?.verifiedBusinesses}
          icon={FiBriefcase}
          color="tertiary"
        />
        <StatCard
          title="Verified Vets"
          value={stats?.verifiedVets}
          icon={FiHeart}
          color="warning"
        />
      </div>

      {/* Revenue — from the payment ledger. Sandbox rows are already excluded
          server-side, and refunds are stored negative, so this sums to net. */}
      <div className={styles.chartsGrid}>
        <Card
          title="Revenue"
          subtitle={`Net EUR from the payment ledger, last ${rangeDays} days`}
          action={
            <Link to="/payments" className={styles.seeAll}>
              View all payments →
            </Link>
          }
        >
          {revenueSeries.length === 0 ? (
            <div className={styles.emptyHint}>
              No payments recorded in this range.
              {subAnalytics && subAnalytics.ledgerRowCount === 0 && (
                <> The ledger is empty — run <Link to="/payments">Backfill</Link> to reconstruct history.</>
              )}
            </div>
          ) : (
            <div className={styles.chart}>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={revenueSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" stroke="#888" fontSize={11} />
                  <YAxis stroke="#888" fontSize={11} />
                  <Tooltip
                    contentStyle={{ background: '#141414', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8 }}
                    formatter={(v) => fmtMoney(v)}
                  />
                  <Bar dataKey="amount" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card title="Recent Payments" subtitle="Latest transactions across all platforms">
          {recentPayments.length === 0 ? (
            <div className={styles.emptyHint}>Nothing yet.</div>
          ) : (
            <div className={styles.feed}>
              {recentPayments.map((p) => (
                <div key={p.id} className={styles.feedRow}>
                  <div className={styles.feedMain}>
                    <span className={styles.feedEmail}>{p.userEmail || p.userId}</span>
                    <span className={styles.feedMeta}>
                      {p.tier || p.kind} · {p.platform}
                      {p.environment === 'sandbox' && ' · sandbox'}
                    </span>
                  </div>
                  <div className={styles.feedAmount}>
                    {fmtMoney(p.amountEur)}
                    <span className={styles.feedDate}>
                      {p.at ? new Date(p.at).toLocaleDateString() : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Subscription mix + net movement */}
      <div className={styles.chartsGrid}>
        <Card title="Active Subscriptions by Tier" subtitle="Currently entitled users">
          {tierRows.length === 0 ? (
            <div className={styles.emptyHint}>No active subscriptions.</div>
          ) : (
            <div className={styles.chart}>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={tierRows} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis type="number" stroke="#888" fontSize={11} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" stroke="#888" fontSize={11} width={110} />
                  <Tooltip
                    contentStyle={{ background: '#141414', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8 }}
                  />
                  <Bar dataKey="count" fill="#22c55e" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card title="Movement (30d)" subtitle="New subscribers vs cancellations">
          <div className={styles.movementGrid}>
            <div className={styles.movementItem}>
              <span className={styles.movementValue}>{subAnalytics?.newVsCancelled?.new ?? '—'}</span>
              <span className={styles.movementLabel}>New</span>
            </div>
            <div className={styles.movementItem}>
              <span className={`${styles.movementValue} ${styles.movementDown}`}>
                {subAnalytics?.newVsCancelled?.cancelled ?? '—'}
              </span>
              <span className={styles.movementLabel}>Cancelled</span>
            </div>
            <div className={styles.movementItem}>
              <span className={styles.movementValue}>{subAnalytics?.newVsCancelled?.net ?? '—'}</span>
              <span className={styles.movementLabel}>Net</span>
            </div>
          </div>
          {subAnalytics?.mrrAtRisk > 0 && (
            <p className={styles.atRisk}>
              {fmtMoney(subAnalytics.mrrAtRisk)} of MRR is from subscriptions that have already
              cancelled auto-renew and will lapse at period end.
            </p>
          )}
          {subAnalytics?.staleActiveCount > 0 && (
            <p className={styles.stale}>
              <strong>{subAnalytics.staleActiveCount} subscriptions still say “active” but their
              period has ended</strong> ({fmtMoney(subAnalytics.staleActiveMrr)} excluded from MRR).
              Store lifecycle notifications aren’t updating these — those users may still be
              getting paid features. <Link to="/subscriptions">Review</Link>
            </p>
          )}
          <div className={styles.platformRow}>
            {platformRows.map((p) => (
              <span key={p.name} className={styles.platformChip}>
                {p.name}: <strong>{p.count}</strong>
              </span>
            ))}
          </div>
        </Card>
      </div>

      {/* Pending Actions */}
      <div className={styles.section}>
        <Card title="Pending Actions" subtitle="Items requiring your attention">
          <div className={styles.actionGrid}>
            <Link to="/businesses?status=pending" className={styles.actionCard}>
              <div className={styles.actionIcon}>
                <FiBriefcase />
              </div>
              <div className={styles.actionContent}>
                <span className={styles.actionValue}>{stats?.pendingBusinesses}</span>
                <span className={styles.actionLabel}>Business Applications</span>
              </div>
              {stats?.pendingBusinesses > 0 && (
                <Badge variant="warning" dot>Pending</Badge>
              )}
            </Link>

            <Link to="/vets?status=pending" className={styles.actionCard}>
              <div className={styles.actionIcon}>
                <FiHeart />
              </div>
              <div className={styles.actionContent}>
                <span className={styles.actionValue}>{stats?.pendingVets}</span>
                <span className={styles.actionLabel}>Vet Applications</span>
              </div>
              {stats?.pendingVets > 0 && (
                <Badge variant="warning" dot>Pending</Badge>
              )}
            </Link>

            <Link to="/reports?status=pending" className={styles.actionCard}>
              <div className={styles.actionIcon}>
                <FiAlertTriangle />
              </div>
              <div className={styles.actionContent}>
                <span className={styles.actionValue}>{stats?.pendingReports}</span>
                <span className={styles.actionLabel}>User Reports</span>
              </div>
              {stats?.pendingReports > 0 && (
                <Badge variant="danger" dot>Action Required</Badge>
              )}
            </Link>
          </div>
        </Card>
      </div>

      {/* Charts */}
      <div className={styles.chartsGrid}>
        <Card
          title="User Growth"
          subtitle={`New users in the last ${rangeDays} days (live)`}
          action={
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <RangePicker />
              <CsvExportButton
                rows={csvRows}
                filename={`dierend-trends-${rangeDays}d`}
                columns={[
                  { key: 'date', label: 'Date' },
                  { key: 'new_users', label: 'New Users' },
                  { key: 'new_orders', label: 'New Orders' },
                ]}
              />
            </div>
          }
        >
          <div className={styles.chart}>
            {growthLoading ? (
              <div className={styles.chartLoading}>
                <div className={styles.spinner} />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={growthData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis
                    dataKey="date"
                    stroke="#666"
                    fontSize={12}
                    tickFormatter={(value) =>
                      rangeDays > 90 ? value.slice(0, 7) : value.slice(5)
                    }
                    minTickGap={32}
                    interval="preserveStartEnd"
                  />
                  <YAxis stroke="#666" fontSize={12} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1a1a1a',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                    }}
                    labelStyle={{ color: '#f5f5f5' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="count"
                    name="New users"
                    stroke="#0ea5e9"
                    strokeWidth={2}
                    dot={{ fill: '#0ea5e9', strokeWidth: 0, r: 4 }}
                    activeDot={{ r: 6, fill: '#38bdf8' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card
          title="Orders Trend"
          subtitle={`New orders in the last ${rangeDays} days (live)`}
          action={<RangePicker />}
        >
          <div className={styles.chart}>
            {ordersLoading ? (
              <div className={styles.chartLoading}>
                <div className={styles.spinner} />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={ordersTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis
                    dataKey="date"
                    stroke="#666"
                    fontSize={12}
                    tickFormatter={(value) =>
                      rangeDays > 90 ? value.slice(0, 7) : value.slice(5)
                    }
                    minTickGap={32}
                    interval="preserveStartEnd"
                  />
                  <YAxis stroke="#666" fontSize={12} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1a1a1a',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                    }}
                    labelStyle={{ color: '#f5f5f5' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="count"
                    name="Orders"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{ fill: '#10b981', strokeWidth: 0, r: 4 }}
                    activeDot={{ r: 6, fill: '#34d399' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card title="Status Breakdown" subtitle="Verification & moderation queues">
          <div className={styles.chart}>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={breakdownData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="label" stroke="#666" fontSize={12} />
                <YAxis stroke="#666" fontSize={12} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: 'rgba(14, 165, 233, 0.06)' }}
                  contentStyle={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: '#f5f5f5' }}
                />
                <Legend wrapperStyle={{ color: '#aaa' }} />
                <Bar dataKey="Pending" stackId="a" fill="#f59e0b" />
                <Bar dataKey="Verified" stackId="a" fill="#10b981" />
                <Bar dataKey="Rejected" stackId="a" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Quick Actions" subtitle="Common admin tasks">
          <div className={styles.quickActions}>
            <Link to="/notifications">
              <Button variant="outline" fullWidth icon={<FiClock />}>
                Send Notification
              </Button>
            </Link>
            <Link to="/users">
              <Button variant="outline" fullWidth icon={<FiUsers />}>
                Manage Users
              </Button>
            </Link>
            <Link to="/businesses">
              <Button variant="outline" fullWidth icon={<FiBriefcase />}>
                Review Businesses
              </Button>
            </Link>
            <Link to="/vets">
              <Button variant="outline" fullWidth icon={<FiHeart />}>
                Review Vets
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
